"use client";

import { createContext, useContext } from "react";

/**
 * The design-editor's dialog seam. Radix portals every DialogContent to
 * `document.body` — correct on a live page, wrong inside the Portal Editor's
 * iframe emulators, where React runs in the parent (CRM) window: a real portal
 * modal mounted in a preview frame would render into the CRM's body — outside
 * the frame, outside the preset stylesheet, outside the emulator's scale
 * transform, and outside the preview's inert guard.
 *
 * A provider around a preview subtree redirects every dialog inside it:
 *  - `container`: the frame's own body — the portal target, which keeps the
 *    modal under the frame's theme root and inside its scale transform;
 *  - `inert: true` renders the modal as PIXELS ONLY. Every primary button in
 *    these modals is a real mutation; the proxy and requireCustomer make them
 *    fail closed today, but that is defense-in-depth, not intent. Inert also
 *    sidesteps the cross-document focus-trap problem wholesale (an inert
 *    subtree cannot receive focus), and PortalModal drops to non-modal so
 *    react-remove-scroll never locks the EDITOR's body over a preview.
 *
 * Null (the default everywhere outside the editor) leaves dialogs exactly as
 * they were.
 */
export interface DialogPreviewTarget {
    container: HTMLElement;
    inert: boolean;
}

export const DialogPreviewContext = createContext<DialogPreviewTarget | null>(null);

export function useDialogPreviewTarget(): DialogPreviewTarget | null {
    return useContext(DialogPreviewContext);
}
